import * as React from "react";
import { type FC } from "react";
import { ComboBox, ComboBoxProps } from "@skbkontur/react-ui/components/ComboBox";

import { FiasAddressType } from "../../../FiasInput/Types/FiasTypes";
import { getFullAddressByFullName } from "../../../FiasInput/Utils/FiasAbbriviations/FiasAbbreviations";
import { useMetrics } from "../../../../../Internals/Metrics/MetricsProviderContext";
import { FIAS_FILL_ADDR_TYPE } from "../../../../../metricsAction";

import { Control } from "./controlsPaths";

type RestComboBoxProps = Omit<
    ComboBoxProps<FiasAddressType>,
    | "getItems"
    | "value"
    | "renderItem"
    | "renderValue"
    | "valueToString"
    | "itemToValue"
    | "onUnexpectedInput"
    | "onValueChange"
    | "drawArrow"
>;

interface AddressTypeFieldProps extends RestComboBoxProps {
    value: string;
    fullSize: boolean;
    controlType: Control;
    onChange: (next: string) => void;
    getSuggests: (query: string) => Promise<FiasAddressType[]>;
}

export const AddressTypeField: FC<AddressTypeFieldProps> = ({
    value,
    fullSize,
    controlType,
    onChange,
    getSuggests,
    ...restProps
}) => {
    const track = useMetrics();

    const onValueChange = ({ fullName, shortName }: FiasAddressType) => {
        onChange(fullSize ? fullName : shortName);
        track(FIAS_FILL_ADDR_TYPE, { handWritten: false, type: controlType, value: shortName });
    };

    const onUnexpectedInput = (input: string) => {
        onChange(input);
        track(FIAS_FILL_ADDR_TYPE, { handWritten: true, type: controlType, value: input });
    };

    return (
        <ComboBox<FiasAddressType>
            getItems={getSuggests}
            value={getFullAddressByFullName(value) || { fullName: value }}
            renderItem={item => `${item.fullName} - ${item.shortName}`}
            renderValue={item => item.fullName}
            valueToString={item => item.fullName}
            itemToValue={item => item.fullName}
            onUnexpectedInput={onUnexpectedInput}
            onValueChange={onValueChange}
            drawArrow={false}
            {...restProps}
        />
    );
};
